import Phaser from 'phaser';
import { getDay } from '../../data/days';
import { MENU_ITEMS } from '../../data/menu';
import { Customer } from '../customers/Customer';
import {
  customerVisualForSequence,
  preloadProductionCustomers,
  registerProductionCustomerAnimations,
  setProductionCustomerState,
  visualStateForCustomer,
} from '../customers/CustomerArt';
import { emitCampaignComplete, emitDayComplete, emitFinaleStep, emitHud, emitToast } from '../events';
import { AudioSystem } from '../systems/AudioSystem';
import { PreparationSystem } from '../systems/PreparationSystem';
import { SaveSystem } from '../systems/SaveSystem';
import { TraySystem } from '../systems/TraySystem';
import { UpgradeSystem, type UpgradeEffects } from '../systems/UpgradeSystem';
import { FevziHelperSystem, type HelperTask } from '../systems/FevziHelperSystem';
import { GORKEM_CUSTOMER_ID, GorkemEventSystem } from '../systems/GorkemEventSystem';
import { EntranceQueue, MAX_VISIBLE_QUEUE } from '../systems/EntranceQueue';
import { Day20FinaleSystem } from '../systems/Day20FinaleSystem';
import { directionFromVector, preloadProductionCharacters, registerProductionCharacterAnimations, type CharacterDirection } from '../characters/CharacterArt';
import type { CustomerWave, DayDefinition, DayResult, MenuItemId, SpecialEventDefinition, TableState } from '../types';
import {
  createCafeBackdrop,
  createCafeForeground,
  createProgressDecor,
  createCustomerVisual,
  createFevzi,
  createTableVisual,
  createYasemin,
  setCharacterAnimation,
  setCharacterDirection,
  setCharacterFacing,
  setCustomerReaction,
  setCustomerSeated,
  setOrderBubbleItems,
  type TableVisual,
  updateTableServiceState,
  updateTableVisual,
} from '../cafe/VisualFactory';

interface TableModel {
  id: number;
  x: number;
  y: number;
  state: TableState;
  customerId: number | null;
  visual: TableVisual;
  helperClaimed: boolean;
}

interface CustomerView {
  model: Customer;
  container: Phaser.GameObjects.Container;
}

interface PendingArrival {
  id: number;
  model: Customer;
  wave: CustomerWave;
}

const QUEUE_SLOTS = [
  { x: 143, y: 300 },
  { x: 143, y: 465 },
  { x: 143, y: 630 },
] as const;

export interface LittleCornerDebug {
  addMoney(amount?: number): void;
  addCafeMoney(amount?: number): void;
  spawnCustomer(order?: MenuItemId | MenuItemId[]): void;
  lowerPatience(): void;
  refillPatience(): void;
  setPatienceFrozen(frozen: boolean): void;
  forceFoodReady(): void;
  setSpeed(speed: number): void;
  triggerGorkem(): void;
  toggleFevzi(): void;
  startHelperAction(): void;
  setHitboxesVisible(visible: boolean): void;
  clearCustomerSelection(): void;
  setCharacterTestState(character: 'yasemin' | 'fevzi' | 'gorkem', state: string, direction: CharacterDirection): void;
  finishDay(): void;
  triggerFinale(): void;
  inspect(): object;
}

declare global {
  interface Window {
    __littleCornerDebug?: LittleCornerDebug;
  }
}

export class CafeScene extends Phaser.Scene {
  private readonly day: DayDefinition;
  private readonly effects: UpgradeEffects;
  private readonly customers = new Map<number, CustomerView>();
  private readonly entranceQueue = new EntranceQueue<PendingArrival>();
  private readonly tables: TableModel[] = [];
  private readonly prep: PreparationSystem;
  private readonly tray = new TraySystem();
  private nextCustomerId = 1;
  private elapsed = 0;
  private nextWaveIndex = 0;
  private earnings = 0;
  private tips = 0;
  private served = 0;
  private hotDrinksServed = 0;
  private lost = 0;
  private combo = 0;
  private maxCombo = 0;
  private specialBonus = 0;
  private selectedCustomerId: number | null = null;
  private player!: Phaser.GameObjects.Container;
  private playerTarget?: Phaser.Math.Vector2;
  private onPlayerArrival?: () => void;
  private actionBusy = false;
  private ticketText!: Phaser.GameObjects.Text;
  private readyGlow!: Phaser.GameObjects.Ellipse;
  private simSpeed = 1;
  private complete = false;
  private hint = 'Kapıdaki ilk misafiri karşıla';
  private fevzi!: Phaser.GameObjects.Container;
  private helper?: FevziHelperSystem;
  private gorkem?: GorkemEventSystem;
  private nextSpecialEventIndex = 0;
  private patienceFrozen = false;
  private hitboxesVisible = false;
  private debugGeometry?: Phaser.GameObjects.Graphics;
  private debugPointerText?: Phaser.GameObjects.Text;
  private lastPointerTarget = 'none';
  private finale?: Day20FinaleSystem;

  constructor(
    private readonly audio: AudioSystem,
    private readonly save: SaveSystem,
    dayId = 1,
  ) {
    super({ key: 'CafeScene' });
    this.day = getDay(dayId);
    this.effects = UpgradeSystem.effects(save.snapshot.upgrades);
    this.prep = new PreparationSystem(this.effects.coffeePrepMultiplier);
  }

  preload(): void {
    this.load.image('cafe-day1-background-v2', `${import.meta.env.BASE_URL}assets/cafe-day1-background-v2.png`);
    preloadProductionCharacters(this);
    preloadProductionCustomers(this);
  }

  create(): void {
    registerProductionCharacterAnimations(this);
    registerProductionCustomerAnimations(this);
    createCafeBackdrop(this);
    this.createAtmosphere();
    createProgressDecor(this, this.day.decorStage ?? 1, this.day.menu);
    this.createStations();
    this.createTables();
    this.player = createYasemin(this, 605, 315);
    this.fevzi = createFevzi(this, 1200, 231);
    this.helper = new FevziHelperSystem(
      this,
      this.fevzi,
      Boolean(this.day.fevziHelper),
      this.effects.helperSpeedMultiplier,
      this.effects.helperCooldown,
      () => this.audio.play('helper'),
    );
    if (this.day.specialEvents?.length) {
      this.gorkem = new GorkemEventSystem(this, this.audio, () => this.handleGorkem(), (message, tone) => emitToast(message, tone));
    }
    createCafeForeground(this);
    this.cameras.main.fadeIn(620, 9, 35, 31);

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < 75 || this.actionBusy) return;
      this.lastPointerTarget = 'floor';
      this.movePlayer(pointer.x, pointer.y);
      this.setHint('Bir misafir ya da nesneye tıkla');
    });

    this.registerDebugTools();
    this.emitHud();
  }

  update(_time: number, deltaMs: number): void {
    this.updateHitboxDebug();
    if (this.complete || (this.finale && this.finale.state !== 'INACTIVE')) return;
    const delta = Math.min(0.05, deltaMs / 1000) * this.simSpeed;
    this.elapsed += delta;
    this.updatePlayer(delta);
    this.spawnScheduledCustomers();
    this.spawnScheduledSpecialEvents();
    this.updateCustomers(delta);
    this.helper?.update(delta, () => this.getHelperTask());
    const newlyReady = this.prep.update(delta);
    if (newlyReady.length > 0) {
      this.audio.play('ready');
      emitToast(`${newlyReady.map((ticket) => MENU_ITEMS[ticket.item].name).join(', ')} hazır!`, 'good');
      this.pulseReadyCounter();
      this.setHint('Hazır siparişi tezgahtan al');
    }
    this.updatePrepDisplay();
    this.checkDayComplete();
  }

  private createAtmosphere(): void {
    for (let i = 0; i < 22; i += 1) {
      const mote = this.add.circle(120 + Math.random() * 610, 155 + Math.random() * 500, 1 + Math.random() * 2, 0xffe1a0, 0.18 + Math.random() * 0.2).setDepth(3);
      this.tweens.add({ targets: mote, y: mote.y - 30 - Math.random() * 50, alpha: 0, duration: 4200 + Math.random() * 3000, repeat: -1, delay: Math.random() * 3000 });
    }
    const steamA = this.add.arc(1083, 157, 12, 210, 335, false, 0, 0).setStrokeStyle(3, 0xfff4dc, 0.48).setDepth(8);
    const steamB = this.add.arc(1100, 163, 10, 210, 335, false, 0, 0).setStrokeStyle(2, 0xfff4dc, 0.35).setDepth(8);
    this.tweens.add({ targets: [steamA, steamB], y: '-=19', alpha: 0.05, duration: 1700, yoyo: true, repeat: -1, ease: 'Sine.inOut', stagger: 240 });

    const lampGlow = this.add.graphics().setDepth(5);
    [818, 925, 1187].forEach((x) => lampGlow.fillStyle(0xffd47b, 0.045).fillCircle(x, 110, 84));
    this.tweens.add({ targets: lampGlow, alpha: 0.64, duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.inOut' });

    const leaves = this.add.container(1238, 271, [
      this.add.ellipse(-8, 0, 16, 36, 0x3f7957, 0.28),
      this.add.ellipse(7, -8, 15, 34, 0x477f5d, 0.24),
    ]).setDepth(7);
    this.tweens.add({ targets: leaves, angle: 2.2, duration: 2300, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
  }

  private createStations(): void {
    this.readyGlow = this.add.ellipse(1092, 271, 205, 60, 0xffd86f, 0).setStrokeStyle(4, 0xffe7a3, 0).setDepth(28);
    const pickup = this.add.container(1092, 266).setDepth(32);
    const boardShadow = this.add.graphics().fillStyle(0x071f1d, 0.25).fillRoundedRect(-105, -21, 210, 53, 14);
    const board = this.add.graphics().fillStyle(0x174a43, 0.96).lineStyle(2, 0xe6bd66).fillRoundedRect(-105, -26, 210, 53, 14).strokeRoundedRect(-105, -26, 210, 53, 14);
    const cupMark = this.add.text(-84, -8, '☕', { fontFamily: 'Arial', fontSize: '14px' }).setOrigin(0.5);
    const title = this.add.text(-64, -15, 'TESLİM', { fontFamily: 'Arial', fontSize: '10px', fontStyle: 'bold', color: '#efca78', letterSpacing: 2 }).setOrigin(0, 0.5);
    this.ticketText = this.add.text(0, 10, 'Sipariş bekleniyor', { fontFamily: 'Arial', fontSize: '11px', color: '#e7dcc0' }).setOrigin(0.5);
    const hitArea = new Phaser.Geom.Rectangle(0, 0, 220, 72);
    pickup.add([boardShadow, board, cupMark, title, this.ticketText]).setSize(220, 72).setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    if (pickup.input) pickup.input.cursor = 'pointer';
    pickup.setData('interactionHitArea', new Phaser.Geom.Rectangle(-110, -36, 220, 72)).setData('debugInteractive', 'pickup');
    pickup.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      this.lastPointerTarget = 'pickup';
      this.handlePickup();
    });
  }

  private createTables(): void {
    const positions = [
      { x: 405, y: 395 },
      { x: 676, y: 520 },
      { x: 900, y: 600 },
      { x: 720, y: 340 },
      { x: 410, y: 620 },
    ];
    positions.slice(0, this.day.tables).forEach((position, index) => {
      const visual = createTableVisual(this, position.x, position.y, index + 1);
      const table: TableModel = { id: index + 1, ...position, state: 'EMPTY', customerId: null, visual, helperClaimed: false };
      visual.container.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();
        this.lastPointerTarget = `table:${table.id}`;
        this.handleTable(table);
      });
      this.tables.push(table);
      updateTableVisual(visual, 'EMPTY');
    });
  }

  private spawnScheduledCustomers(): void {
    while (this.nextWaveIndex < this.day.waves.length && this.elapsed >= this.day.waves[this.nextWaveIndex].at) {
      this.spawnCustomer(this.day.waves[this.nextWaveIndex]);
      this.nextWaveIndex += 1;
    }
    this.releasePendingCustomer();
  }

  private spawnScheduledSpecialEvents(): void {
    const events = this.day.specialEvents ?? [];
    while (this.nextSpecialEventIndex < events.length && this.elapsed >= events[this.nextSpecialEventIndex].at) {
      if (this.entranceQueue.visible.length >= MAX_VISIBLE_QUEUE) return;
      const started = this.gorkem?.start(events[this.nextSpecialEventIndex]);
      if (!started) return;
      this.nextSpecialEventIndex += 1;
    }
  }

  private spawnCustomer(wave: CustomerWave): void {
    const model = new Customer(this.nextCustomerId++, wave.archetype, wave.order, this.effects.patienceBonus);
    this.entranceQueue.enqueue({ id: model.id, model, wave });
    this.releasePendingCustomer();
  }

  private releasePendingCustomer(): void {
    const pending = this.entranceQueue.releaseNext(this.elapsed, this.isGorkemEntranceReserved() ? MAX_VISIBLE_QUEUE - 1 : MAX_VISIBLE_QUEUE);
    if (!pending) return;
    const slotIndex = this.entranceQueue.slotIndex(pending.id);
    const slot = QUEUE_SLOTS[slotIndex];
    const { model } = pending;
    const visualId = customerVisualForSequence(model.id, this.day.id);
    const container = createCustomerVisual(this, 48, 176, pending.wave.archetype, model.config.color, visualId);
    setProductionCustomerState(container, 'arrive', directionFromVector(slot.x - 48, slot.y - 176));
    container.setData('queueSlot', slotIndex);
    container.setAlpha(0).setScale(0.75);
    container.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      this.lastPointerTarget = `customer:${model.id}`;
      this.handleCustomer(model.id);
    });
    this.customers.set(model.id, { model, container });
    this.audio.play('arrival');
    this.showArrivalCue();
    this.tweens.add({
      targets: container,
      x: slot.x,
      y: slot.y,
      alpha: 1,
      scale: 1,
      duration: 850,
      ease: 'Back.out',
      onComplete: () => {
        if (model.stateMachine.state === 'ARRIVING') model.stateMachine.transition('WAITING_FOR_SEAT');
        this.syncCustomerArt({ model, container });
        this.updateCustomerBubble(model);
        this.setHint('Misafire tıkla, sonra boş bir masa seç');
      },
    });
  }

  private isGorkemEntranceReserved(): boolean {
    const nextEvent = this.day.specialEvents?.[this.nextSpecialEventIndex];
    const eventIsDue = Boolean(nextEvent && this.elapsed >= nextEvent.at && (!this.gorkem || this.gorkem.state === 'INACTIVE'));
    const gorkemIsEntering = this.gorkem ? ['ENTERING', 'WALKING_TO_TABLE'].includes(this.gorkem.state) : false;
    return eventIsDue || gorkemIsEntering;
  }

  private advanceEntranceQueue(): void {
    this.entranceQueue.visible.forEach((id, slotIndex) => {
      const view = this.customers.get(id);
      if (!view) return;
      const slot = QUEUE_SLOTS[slotIndex];
      view.container.setData('queueSlot', slotIndex);
      if (Math.abs(view.container.x - slot.x) < 1 && Math.abs(view.container.y - slot.y) < 1) return;
      setProductionCustomerState(view.container, 'walk', directionFromVector(slot.x - view.container.x, slot.y - view.container.y, view.container.getData('direction') ?? 'down'));
      this.tweens.add({
        targets: view.container,
        x: slot.x,
        y: slot.y,
        duration: 320,
        ease: 'Sine.inOut',
        onComplete: () => {
          view.container.setData('direction', 'down');
          this.syncCustomerArt(view);
        },
      });
    });
  }

  private handleCustomer(customerId: number): void {
    const view = this.customers.get(customerId);
    if (!view || this.actionBusy) return;
    if (view.model.stateMachine.state !== 'WAITING_FOR_SEAT') {
      if (view.model.tableId) this.handleTable(this.tables[view.model.tableId - 1]);
      return;
    }
    this.actionBusy = true;
    this.movePlayer(view.container.x + 55, view.container.y, () => {
      setCharacterAnimation(this.player, 'greet');
      this.time.delayedCall(420, () => setCharacterAnimation(this.player, 'idle'));
      this.actionBusy = false;
      if (view.model.stateMachine.state !== 'WAITING_FOR_SEAT') return;
      this.selectedCustomerId = customerId;
      this.tables.forEach((table) => updateTableVisual(table.visual, table.state, table.state === 'EMPTY'));
      this.setHint('Şimdi boş bir masaya tıkla');
      emitToast('Hoş geldiniz! Masanızı seçebilirsiniz.', 'info');
    });
  }

  private handleTable(table: TableModel): void {
    if (this.actionBusy) return;
    if (table.helperClaimed) {
      emitToast('Fevzi bu masayla ilgileniyor.', 'info');
      return;
    }
    if (table.state === 'EMPTY' && this.selectedCustomerId) {
      this.seatCustomer(table, this.selectedCustomerId);
      return;
    }
    if (table.state === 'WAITING_FOR_CLEANUP') {
      this.cleanTable(table);
      return;
    }
    if (!table.customerId) {
      emitToast('Bu masa yeni bir misafir için hazır.', 'info');
      return;
    }
    const view = this.customers.get(table.customerId);
    if (!view) return;
    const state = view.model.stateMachine.state;
    if (state === 'WAITING_TO_ORDER') this.takeOrder(table, view);
    else if (state === 'WAITING_FOR_FOOD') this.serveOrder(table, view);
    else if (state === 'WAITING_TO_PAY') this.collectPayment(table, view);
    else if (state === 'DECIDING') emitToast('Misafir menüye göz atıyor…', 'info');
    else if (state === 'EATING') emitToast('Afiyet olsun! Birazdan ödeme hazır.', 'info');
  }

  private seatCustomer(table: TableModel, customerId: number): void {
    const view = this.customers.get(customerId);
    if (!view || view.model.stateMachine.state !== 'WAITING_FOR_SEAT') return;
    this.actionBusy = true;
    this.movePlayer(table.x - 76, table.y + 15, () => {
      if (view.model.stateMachine.state !== 'WAITING_FOR_SEAT') {
        this.actionBusy = false;
        return;
      }
      view.model.stateMachine.transition('WALKING_TO_TABLE');
      this.entranceQueue.removeVisible(view.model.id);
      this.advanceEntranceQueue();
      setProductionCustomerState(
        view.container,
        'walk',
        directionFromVector(table.x - view.container.x, table.y - 31 - view.container.y, view.container.getData('direction') ?? 'down'),
      );
      view.model.tableId = table.id;
      table.state = 'ASSIGNED';
      table.customerId = customerId;
      updateTableVisual(table.visual, table.state);
      this.selectedCustomerId = null;
      this.tweens.add({
        targets: view.container,
        x: table.x,
        y: table.y - 31,
        scale: 0.88,
        duration: 1000,
        ease: 'Sine.inOut',
        onComplete: () => {
          view.model.stateMachine.transition('DECIDING');
          setCustomerSeated(view.container, true);
          view.container.setData('direction', table.id === 1 ? 'right' : 'left');
          view.container.setDepth(table.visual.container.depth - 1);
          view.model.decisionRemaining = 2.4 + Math.random() * 1.4;
          table.state = 'OCCUPIED';
          updateTableVisual(table.visual, table.state);
          this.updateCustomerBubble(view.model);
          this.actionBusy = false;
          this.setHint('Sipariş balonu çıkınca masaya tıkla');
        },
      });
    });
  }

  private takeOrder(table: TableModel, view: CustomerView): void {
    this.actionBusy = true;
    this.movePlayer(table.x - 88, table.y + 5, () => {
      if (view.model.stateMachine.state !== 'WAITING_TO_ORDER') {
        this.actionBusy = false;
        return;
      }
      this.showActionPop(table.x, table.y - 92, '✎', 0x5f9d78);
      setCharacterAnimation(this.player, 'takeOrder');
      this.time.delayedCall(this.effects.orderDuration * 1000, () => {
        if (view.model.stateMachine.state !== 'WAITING_TO_ORDER') {
          this.actionBusy = false;
          return;
        }
        view.model.stateMachine.transition('WAITING_FOR_FOOD');
        this.prep.submitOrder(view.model.id, view.model.orders);
        this.audio.play('order');
        setCharacterAnimation(this.player, 'idle');
        this.actionBusy = false;
        this.updateCustomerBubble(view.model);
        this.updatePrepDisplay();
        this.setHint('Sipariş hazırlanıyor; hazır olunca tezgâha tıkla');
        emitToast(`${view.model.orders.map((id) => MENU_ITEMS[id].name).join(' + ')} siparişi mutfağa gönderildi.`, 'good');
      });
    });
  }

  private handlePickup(): void {
    if (this.actionBusy) return;
    if (this.tray.isFull) {
      emitToast('Tepsin dolu; önce siparişi servis et.', 'warning');
      return;
    }
    const preferredCustomerId = this.tray.items[0]?.customerId;
    const ticket = this.prep.tickets.find((item) => item.ready && (preferredCustomerId === undefined || item.customerId === preferredCustomerId))
      ?? this.prep.tickets.find((item) => item.ready);
    if (!ticket) {
      emitToast(this.prep.tickets.length ? 'Sipariş henüz hazırlanıyor.' : 'Hazır sipariş yok.', 'info');
      return;
    }
    this.actionBusy = true;
    this.movePlayer(990, 295, () => {
      setCharacterAnimation(this.player, 'pickupTray', 'right');
      const ready = this.prep.takeFirstReady(preferredCustomerId);
      if (ready) {
        this.tray.add({ customerId: ready.customerId, item: ready.item });
        this.updateTrayVisual();
        setCharacterAnimation(this.player, 'carryIdle', 'right');
        this.setHint('Tepsiyi doğru masaya götür');
        emitToast(`${MENU_ITEMS[ready.item].name} tepside.`, 'good');
      }
      this.actionBusy = false;
      this.updatePrepDisplay();
    });
  }

  private serveOrder(table: TableModel, view: CustomerView): void {
    const carriedCount = this.tray.countForCustomer(view.model.id);
    if (carriedCount < view.model.orders.length) {
      if (carriedCount > 0) {
        emitToast(`Bu sipariş için ${view.model.orders.length - carriedCount} ürün daha gerekiyor.`, 'warning');
        return;
      }
      emitToast(this.tray.items.length ? 'Bu tepside başka masanın siparişi var.' : 'Önce hazır siparişi tezgahtan al.', 'warning');
      return;
    }
    this.actionBusy = true;
    this.movePlayer(table.x - 90, table.y + 8, () => {
      if (view.model.stateMachine.state !== 'WAITING_FOR_FOOD') {
        this.actionBusy = false;
        return;
      }
      this.tray.takeAllForCustomer(view.model.id);
      this.updateTrayVisual();
      setCharacterAnimation(this.player, 'serve');
      view.model.stateMachine.transition('EATING');
      view.model.eatingRemaining = 5.8 + Math.random() * 1.8;
      this.audio.play('serve');
      this.showServeSparkles(table.x, table.y - 24);
      this.actionBusy = false;
      this.served += 1;
      this.hotDrinksServed += view.model.orders.filter((id) => MENU_ITEMS[id].preparationKind === 'HOT_DRINK').length;
      if (view.model.patienceRatio > 0.58) {
        this.combo += 1;
        this.maxCombo = Math.max(this.maxCombo, this.combo);
        emitToast(this.combo > 1 ? `HIZLI SERVİS · ${this.combo} KOMBO!` : 'HIZLI SERVİS · + bahşiş', 'good');
        if (this.combo > 1) this.showComboPop(table.x, table.y - 126);
      } else {
        this.combo = 0;
      }
      this.updateCustomerBubble(view.model);
      setCustomerReaction(view.container, 'happy');
      view.container.setData('customerArtHoldUntil', this.time.now + 500);
      this.time.delayedCall(520, () => {
        if (view.model.stateMachine.state === 'EATING') this.syncCustomerArt(view);
      });
      this.setHint('Misafir yemeğini bitirince ödemeyi al');
      this.emitHud();
      this.time.delayedCall(520, () => setCharacterAnimation(this.player, 'idle'));
    });
  }

  private collectPayment(table: TableModel, view: CustomerView): void {
    this.actionBusy = true;
    this.movePlayer(table.x - 85, table.y + 12, () => {
      if (view.model.stateMachine.state !== 'WAITING_TO_PAY') {
        this.actionBusy = false;
        return;
      }
      const orderValue = view.model.orders.reduce((sum, id) => sum + MENU_ITEMS[id].price, 0);
      setCharacterAnimation(this.player, 'payment');
      const speedBonus = view.model.calculateSpeedBonus(orderValue);
      const tip = view.model.calculateTip() + Math.min(4, this.combo);
      const totalPayment = orderValue + speedBonus + tip;
      this.earnings += totalPayment;
      this.tips += tip;
      this.audio.play('coin');
      this.showMoneyPop(table.x, table.y - 90, `+₺${totalPayment}  ⚡ HIZ +₺${speedBonus}`);
      setCustomerReaction(view.container, 'happy');
      view.model.stateMachine.transition('LEAVING');
      table.state = 'WAITING_FOR_CLEANUP';
      table.customerId = null;
      updateTableVisual(table.visual, table.state);
      this.actionBusy = false;
      this.updateCustomerBubble(view.model);
      this.leaveCafe(view, false);
      this.setHint('Masayı temizle, yeni misafire hazırla');
      this.emitHud();
      this.time.delayedCall(480, () => setCharacterAnimation(this.player, 'idle'));
    });
  }

  private cleanTable(table: TableModel): void {
    this.actionBusy = true;
    this.movePlayer(table.x - 82, table.y + 16, () => {
      setCharacterAnimation(this.player, 'clean', 'right');
      const cloth = this.add.text(table.x, table.y - 25, '✦', { fontSize: '38px', color: '#fff1a8' }).setOrigin(0.5).setDepth(30);
      this.tweens.add({ targets: cloth, angle: 180, scale: 1.4, alpha: 0, duration: 750, onComplete: () => cloth.destroy() });
      this.time.delayedCall(this.effects.cleanupDuration * 1000, () => {
        table.state = 'EMPTY';
        updateTableVisual(table.visual, table.state);
        this.audio.play('clean');
        setCharacterAnimation(this.player, 'idle');
        this.actionBusy = false;
        this.setHint(this.nextWaveIndex < this.day.waves.length || this.entranceQueue.pending.length > 0 ? 'Sıradaki misafir için hazır!' : 'Son kontroller…');
      });
    });
  }

  private getHelperTask(): HelperTask | undefined {
    const dirty = this.tables.find((table) => table.state === 'WAITING_FOR_CLEANUP' && !table.helperClaimed);
    if (dirty) {
      dirty.helperClaimed = true;
      return {
        kind: 'CLEANING',
        animation: 'cleanTable',
        x: dirty.x + 82,
        y: dirty.y + 10,
        run: () => {
          if (dirty.state === 'WAITING_FOR_CLEANUP') {
            dirty.state = 'EMPTY';
            dirty.customerId = null;
            updateTableVisual(dirty.visual, dirty.state);
            this.audio.play('clean');
            this.showActionPop(dirty.x, dirty.y - 55, '✦', 0x4f8f83);
            emitToast(`Fevzi Masa ${dirty.id}’i temizledi.`, 'good');
          }
          dirty.helperClaimed = false;
        },
      };
    }
    const pending = this.prep.tickets.find((ticket) => !ticket.ready);
    if (pending) {
      return {
        kind: 'PREPARING',
        animation: MENU_ITEMS[pending.item].preparationKind === 'COLD_DRINK' ? 'prepareColdDrink' : 'prepareCoffee',
        x: 1055,
        y: 275,
        run: () => {
          const assisted = this.prep.accelerateFirstPending(1.6);
          if (assisted) {
            this.showActionPop(1070, 222, '⚡', 0xc79235);
            emitToast(`Fevzi ${MENU_ITEMS[assisted.item].name} hazırlığına destek oldu.`, 'good');
            this.updatePrepDisplay();
          }
        },
      };
    }
    return undefined;
  }

  private handleGorkem(): void {
    if (!this.gorkem || this.actionBusy) return;
    const definition = this.gorkem.event;
    if (!definition) return;
    if (this.gorkem.state === 'WAITING_FOR_ORDER') {
      this.actionBusy = true;
      this.movePlayer(1030, 470, () => {
        this.time.delayedCall(this.effects.orderDuration * 1000, () => {
          if (this.gorkem?.orderTaken()) {
            this.prep.submit(GORKEM_CUSTOMER_ID, definition.order);
            this.showActionPop(1120, 402, '✓', 0x65518c);
            this.updatePrepDisplay();
            this.setHint('Görkem’in özel siparişini hazırla');
          }
          this.actionBusy = false;
        });
      });
      return;
    }
    if (this.gorkem.state === 'WAITING_FOR_FOOD') {
      const carried = this.tray.items.find((item) => item.customerId === GORKEM_CUSTOMER_ID);
      if (!carried) {
        emitToast('Görkem’in siparişini önce teslim tezgâhından al.', 'warning');
        return;
      }
      this.actionBusy = true;
      this.movePlayer(1030, 470, () => {
        this.tray.takeForCustomer(GORKEM_CUSTOMER_ID);
        this.updateTrayVisual();
        const bonus = this.gorkem?.served() ?? 0;
        this.specialBonus += bonus;
        this.showMoneyPop(1120, 390, `HEDİYE +₺${bonus}`);
        this.showServeSparkles(1120, 448);
        this.setHint('Görkem el sallarken servise devam et');
        this.actionBusy = false;
      });
      return;
    }
    emitToast('Görkem küçük köşesinde keyifle bekliyor.', 'info');
  }

  private updateCustomers(delta: number): void {
    this.customers.forEach((view) => {
      const { model, container } = view;
      if (!this.patienceFrozen && model.tick(delta) && !['LEAVING', 'ANGRY_LEAVING'].includes(model.stateMachine.state)) {
        this.makeAngry(view);
        return;
      }
      if (model.stateMachine.state === 'DECIDING') {
        model.decisionRemaining -= delta;
        if (model.decisionRemaining <= 0) {
          model.stateMachine.transition('WAITING_TO_ORDER');
          this.updateCustomerBubble(model);
          this.audio.play('arrival');
          this.setHint('Siparişi almak için masaya tıkla');
        }
      } else if (model.stateMachine.state === 'EATING') {
        model.eatingRemaining -= delta;
        if (model.eatingRemaining <= 0) {
          model.stateMachine.transition('WAITING_TO_PAY');
          this.updateCustomerBubble(model);
          this.audio.play('coin');
          this.setHint('Hesabı almak için masaya tıkla');
        }
      }
      this.syncCustomerArt(view);
      const patienceFill = container.getData('patienceFill') as Phaser.GameObjects.Rectangle;
      patienceFill.setScale(model.patienceRatio, 1);
      patienceFill.setFillStyle(model.patienceRatio > 0.55 ? 0x70c58e : model.patienceRatio > 0.25 ? 0xf0bc56 : 0xd85446);
    });
  }

  private makeAngry(view: CustomerView): void {
    const { model } = view;
    if (!model.stateMachine.canTransition('ANGRY_LEAVING')) return;
    model.stateMachine.transition('ANGRY_LEAVING');
    if (this.entranceQueue.removeVisible(model.id)) this.advanceEntranceQueue();
    this.prep.cancelCustomer(model.id);
    this.tray.takeAllForCustomer(model.id);
    this.updateTrayVisual();
    if (model.tableId) {
      const table = this.tables[model.tableId - 1];
      table.customerId = null;
      table.state = 'WAITING_FOR_CLEANUP';
      updateTableVisual(table.visual, table.state);
    }
    if (this.selectedCustomerId === model.id) this.selectedCustomerId = null;
    this.lost += 1;
    this.combo = 0;
    this.audio.play('angry');
    setCustomerReaction(view.container, 'angry');
    this.updateCustomerBubble(model);
    emitToast('Misafirin sabrı tükendi.', 'warning');
    this.leaveCafe(view, true);
    this.emitHud();
  }

  private leaveCafe(view: CustomerView, angry: boolean): void {
    setCustomerSeated(view.container, false);
    setProductionCustomerState(view.container, angry ? 'angry' : 'leaving', 'left');
    if (!view.container.getData('productionCustomerArt')) setCharacterFacing(view.container, -1);
    view.container.setDepth(25);
    this.tweens.add({
      targets: view.container,
      x: 60,
      y: 182,
      scale: 0.75,
      alpha: angry ? 0.6 : 0.9,
      angle: angry ? -4 : 0,
      duration: 1150,
      ease: 'Sine.inOut',
      onComplete: () => {
        this.customers.delete(view.model.id);
        view.container.destroy(true);
      },
    });
  }

  private updateCustomerBubble(model: Customer): void {
    const view = this.customers.get(model.id);
    if (!view) return;
    const bubble = view.container.getData('bubble') as Phaser.GameObjects.Container;
    const text = view.container.getData('bubbleText') as Phaser.GameObjects.Text;
    const state = model.stateMachine.state;
    const show = ['WAITING_FOR_SEAT', 'DECIDING', 'WAITING_TO_ORDER', 'WAITING_FOR_FOOD', 'EATING', 'WAITING_TO_PAY', 'ANGRY_LEAVING'].includes(state);
    const lastState = bubble.getData('lastState') as string;
    const orderVisible = state === 'WAITING_TO_ORDER';
    setOrderBubbleItems(bubble, orderVisible ? model.orders : []);
    text.setVisible(!orderVisible);
    const labels: Partial<Record<typeof state, string>> = {
      WAITING_FOR_SEAT: '↘',
      DECIDING: '…',
      WAITING_FOR_FOOD: '◷',
      EATING: '♥',
      WAITING_TO_PAY: '₺',
      ANGRY_LEAVING: '!',
    };
    text.setText(labels[state] ?? '');
    if (show && lastState !== state) {
      bubble.setData('lastState', state).setVisible(true).setAlpha(0).setScale(0.72);
      this.tweens.add({ targets: bubble, alpha: 1, scale: 1, duration: 230, ease: 'Back.out' });
    } else if (!show && bubble.visible) {
      this.tweens.add({ targets: bubble, alpha: 0, scale: 0.82, duration: 150, onComplete: () => bubble.setVisible(false) });
    }
    if (model.tableId && this.tables[model.tableId - 1]?.state === 'OCCUPIED') {
      updateTableServiceState(this.tables[model.tableId - 1].visual, state);
    }
    this.syncCustomerArt(view);
  }

  private syncCustomerArt(view: CustomerView): void {
    if (!view.container.getData('productionCustomerArt')) return;
    if ((view.container.getData('customerArtHoldUntil') as number | undefined ?? 0) > this.time.now) return;
    const visualState = visualStateForCustomer(view.model.stateMachine.state, view.model.order, view.model.patienceRatio);
    const current = view.container.getData('customerVisualState') as string | undefined;
    if (current === visualState) return;
    setProductionCustomerState(view.container, visualState, view.container.getData('direction') ?? 'down', true);
  }

  private showArrivalCue(): void {
    const ring = this.add.circle(55, 193, 26, 0xf3c96f, 0).setStrokeStyle(4, 0xf3c96f, 0.8).setDepth(45);
    const spark = this.add.text(55, 193, '✦', { fontFamily: 'Arial', fontSize: '18px', color: '#ffe5a4', stroke: '#173f3a', strokeThickness: 3 }).setOrigin(0.5).setDepth(46);
    this.tweens.add({ targets: ring, scale: 1.9, alpha: 0, duration: 780, ease: 'Cubic.out', onComplete: () => ring.destroy() });
    this.tweens.add({ targets: spark, y: 174, alpha: 0, duration: 850, ease: 'Cubic.out', onComplete: () => spark.destroy() });
  }

  private showActionPop(x: number, y: number, icon: string, color: number): void {
    const disk = this.add.circle(x, y, 18, color, 0.96).setStrokeStyle(3, 0xffe5a1, 0.9).setDepth(48);
    const mark = this.add.text(x, y - 1, icon, { fontFamily: 'Arial', fontSize: '18px', fontStyle: 'bold', color: '#fff6dc' }).setOrigin(0.5).setDepth(49);
    this.tweens.add({ targets: [disk, mark], y: y - 32, alpha: 0, scale: 1.2, duration: 780, ease: 'Cubic.out', onComplete: () => { disk.destroy(); mark.destroy(); } });
  }

  private showServeSparkles(x: number, y: number): void {
    for (let index = 0; index < 6; index += 1) {
      const angle = (Math.PI * 2 * index) / 6;
      const sparkle = this.add.text(x, y, index % 2 ? '•' : '✦', { fontFamily: 'Arial', fontSize: index % 2 ? '18px' : '14px', color: index % 2 ? '#fff0b0' : '#e5ad4d' }).setOrigin(0.5).setDepth(47);
      this.tweens.add({ targets: sparkle, x: x + Math.cos(angle) * 52, y: y + Math.sin(angle) * 34, alpha: 0, scale: 1.4, duration: 720, ease: 'Cubic.out', onComplete: () => sparkle.destroy() });
    }
  }

  private showComboPop(x: number, y: number): void {
    const text = this.add.text(x, y, `${this.combo} KOMBO`, {
      fontFamily: 'Arial', fontSize: '16px', fontStyle: 'bold', color: '#ffe69b',
      backgroundColor: '#a64f3d', padding: { x: 12, y: 7 }, stroke: '#6d332a', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(50);
    this.tweens.add({ targets: text, y: y - 42, scale: 1.16, alpha: 0, duration: 1050, ease: 'Back.out', onComplete: () => text.destroy() });
  }

  private updatePrepDisplay(): void {
    if (!this.ticketText) return;
    if (this.prep.tickets.length === 0) {
      this.ticketText.setText('Sipariş bekleniyor').setColor('#d8d2b8');
      this.readyGlow.setFillStyle(0xffd86f, 0).setStrokeStyle(5, 0xffe7a3, 0);
      return;
    }
    const shown = this.prep.tickets.slice(0, 3).map((ticket) => `${MENU_ITEMS[ticket.item].shortCode} ${ticket.ready ? '✓' : Math.ceil(ticket.remaining)}`);
    const overflow = this.prep.tickets.length > 3 ? ` +${this.prep.tickets.length - 3}` : '';
    this.ticketText.setText(`${shown.join(' · ')}${overflow}`);
    const hasReady = this.prep.tickets.some((ticket) => ticket.ready);
    this.ticketText.setColor(hasReady ? '#ffe074' : '#f8e8bd');
    this.readyGlow.setFillStyle(0xffd86f, hasReady ? 0.18 : 0).setStrokeStyle(5, 0xffe7a3, hasReady ? 0.75 : 0);
  }

  private pulseReadyCounter(): void {
    this.tweens.add({ targets: this.readyGlow, scaleX: 1.12, scaleY: 1.12, duration: 240, yoyo: true, repeat: 3, ease: 'Sine.inOut' });
  }

  private updateTrayVisual(): void {
    const tray = this.player.getData('tray') as Phaser.GameObjects.Container;
    tray.setVisible(!this.player.getData('productionArt') && this.tray.items.length > 0);
    const status = this.player.getData('trayStatus') as Phaser.GameObjects.Container;
    const statusText = this.player.getData('trayStatusText') as Phaser.GameObjects.Text;
    status.setVisible(this.tray.items.length > 0);
    statusText.setText(this.tray.items.map((entry) => MENU_ITEMS[entry.item].shortCode).join(' + '));
    const icon = tray.list.find((item) => item instanceof Phaser.GameObjects.Text) as Phaser.GameObjects.Text | undefined;
    if (this.tray.items.length) icon?.setText(this.tray.items.map((entry) => MENU_ITEMS[entry.item].shortCode).join('+'));
  }

  private movePlayer(x: number, y: number, onArrival?: () => void): void {
    this.playerTarget = new Phaser.Math.Vector2(Phaser.Math.Clamp(x, 110, 1180), Phaser.Math.Clamp(y, 285, 665));
    this.onPlayerArrival = onArrival;
  }

  private updatePlayer(delta: number): void {
    if (!this.playerTarget) return;
    const dx = this.playerTarget.x - this.player.x;
    const dy = this.playerTarget.y - this.player.y;
    const distance = Math.hypot(dx, dy);
    const speed = (this.tray.items.length > 0 ? 205 : 245) * this.effects.movementMultiplier;
    if (distance <= speed * delta + 2) {
      this.player.setPosition(this.playerTarget.x, this.playerTarget.y);
      const sprite = this.player.getData('sprite') as Phaser.GameObjects.Container;
      sprite?.setAngle(0);
      setCharacterAnimation(this.player, this.tray.items.length > 0 ? 'carryIdle' : 'idle');
      this.playerTarget = undefined;
      const callback = this.onPlayerArrival;
      this.onPlayerArrival = undefined;
      callback?.();
      return;
    }
    this.player.x += (dx / distance) * speed * delta;
    this.player.y += (dy / distance) * speed * delta;
    const previous = this.player.getData('direction') ?? 'down';
    const direction = directionFromVector(dx, dy, previous);
    setCharacterDirection(this.player, direction);
    setCharacterAnimation(this.player, this.tray.items.length > 0 ? 'carryWalk' : 'walk', direction, true);
    const sprite = this.player.getData('sprite') as Phaser.GameObjects.Container;
    sprite?.setAngle(Math.sin(this.time.now * 0.018) * 2.2);
    this.player.setDepth(12 + this.player.y / 20);
  }

  private showMoneyPop(x: number, y: number, label: string): void {
    const text = this.add.text(x, y, label, {
      fontFamily: 'Arial', fontSize: '20px', fontStyle: 'bold', color: '#fff1a3',
      stroke: '#173f3a', strokeThickness: 5,
    }).setOrigin(0.5).setDepth(40);
    this.tweens.add({ targets: text, y: y - 55, scale: 1.18, alpha: 0, duration: 1300, ease: 'Cubic.out', onComplete: () => text.destroy() });
  }

  private setHint(hint: string): void {
    this.hint = hint;
    this.emitHud();
  }

  private setHitboxesVisible(visible: boolean): void {
    this.hitboxesVisible = visible;
    if (!visible) {
      this.debugGeometry?.clear().setVisible(false);
      this.debugPointerText?.setVisible(false);
      return;
    }
    this.debugGeometry ??= this.add.graphics().setDepth(200);
    this.debugPointerText ??= this.add.text(12, 92, '', {
      fontFamily: 'ui-monospace, monospace',
      fontSize: '13px',
      color: '#fff7ce',
      backgroundColor: '#123d38',
      padding: { x: 8, y: 5 },
    }).setDepth(201).setScrollFactor(0);
    this.debugGeometry.setVisible(true);
    this.debugPointerText.setVisible(true);
    this.updateHitboxDebug();
  }

  private updateHitboxDebug(): void {
    if (!this.hitboxesVisible || !this.debugGeometry || !this.debugPointerText) return;
    const graphics = this.debugGeometry.clear();
    const drawTarget = (target: Phaser.GameObjects.Container): void => {
      if (!target.visible) return;
      const sprite = target.getData('sprite') as Phaser.GameObjects.Container | Phaser.GameObjects.Sprite | undefined;
      const localVisualBounds = target.getData('visualLocalBounds') as Phaser.Geom.Rectangle | undefined;
      const visualBounds = localVisualBounds
        ? new Phaser.Geom.Rectangle(
          target.x + localVisualBounds.x * target.scaleX,
          target.y + localVisualBounds.y * target.scaleY,
          localVisualBounds.width * target.scaleX,
          localVisualBounds.height * target.scaleY,
        )
        : sprite?.getBounds();
      if (visualBounds) graphics.lineStyle(2, 0x42e8ff, 0.95).strokeRectShape(visualBounds);
      const area = target.getData('interactionHitArea') as Phaser.Geom.Rectangle | undefined;
      if (area) {
        graphics.lineStyle(2, 0xffd34d, 1).strokeRect(
          target.x + area.x * target.scaleX,
          target.y + area.y * target.scaleY,
          area.width * target.scaleX,
          area.height * target.scaleY,
        );
      }
      graphics.lineStyle(2, 0xff4fd8, 1)
        .lineBetween(target.x - 8, target.y, target.x + 8, target.y)
        .lineBetween(target.x, target.y - 8, target.x, target.y + 8);
    };
    this.customers.forEach(({ container }) => drawTarget(container));
    if (this.gorkem?.visual.visible) drawTarget(this.gorkem.visual);
    const pointer = this.input.activePointer;
    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    graphics.lineStyle(1, 0xffffff, 0.8).strokeCircle(world.x, world.y, 6);
    this.debugPointerText.setText(`POINTER world: ${world.x.toFixed(1)}, ${world.y.toFixed(1)}`);
  }

  private emitHud(): void {
    emitHud({ day: this.day.id, earnings: this.earnings, goal: this.day.goal, combo: this.combo, served: this.served, lost: this.lost, hint: this.hint });
  }

  private checkDayComplete(): void {
    if (this.nextWaveIndex < this.day.waves.length || this.entranceQueue.hasOutstandingCustomers || this.customers.size > 0 || this.prep.tickets.length > 0 || this.tray.items.length > 0) return;
    if (this.nextSpecialEventIndex < (this.day.specialEvents?.length ?? 0)) return;
    if (this.gorkem && !['INACTIVE', 'COMPLETE'].includes(this.gorkem.state)) return;
    if (this.tables.some((table) => table.state !== 'EMPTY')) return;
    const success = this.earnings >= this.day.goal;
    const objective = this.day.bonusObjective;
    const objectiveComplete = !objective ? this.lost === 0 : (() => {
      if (objective.kind === 'NO_LOST') return this.lost === 0;
      if (objective.kind === 'SERVE') return this.served >= objective.target;
      if (objective.kind === 'COMBO') return this.maxCombo >= objective.target;
      if (objective.kind === 'TIPS') return this.tips >= objective.target;
      if (objective.kind === 'HOT_DRINKS') return this.hotDrinksServed >= objective.target;
      return this.tables.every((table) => table.state === 'EMPTY');
    })();
    const bonus = (success && objectiveComplete ? (objective?.reward ?? 12) : 0) + this.specialBonus;
    const result: DayResult = {
      day: this.day.id,
      earnings: this.earnings,
      tips: this.tips,
      served: this.served,
      lost: this.lost,
      bonus,
      maxCombo: this.maxCombo,
      stars: success ? (this.lost === 0 && this.earnings >= this.day.goal + 20 ? 3 : this.lost === 0 ? 2 : 1) : 0,
      success,
    };
    if (this.day.finale && success) {
      this.startFinale(result);
      return;
    }
    this.finishDay(result);
  }

  private startFinale(result?: DayResult): void {
    if (this.finale && this.finale.state !== 'INACTIVE') return;
    this.input.enabled = false;
    this.playerTarget = undefined;
    this.onPlayerArrival = undefined;
    if (this.helper) this.helper.enabled = false;
    this.tables.forEach((table) => table.visual.stateBadge.setVisible(false));
    const finalResult = result ?? {
      day: 20,
      earnings: Math.max(this.earnings, this.day.goal),
      tips: this.tips,
      served: this.served,
      lost: this.lost,
      bonus: 20 + this.specialBonus,
      maxCombo: this.maxCombo,
      stars: this.lost === 0 ? 3 : 2,
      success: true,
    };
    this.finale = new Day20FinaleSystem(
      this,
      this.player,
      this.fevzi,
      (state, message) => emitFinaleStep(state, message),
      () => {
        this.finishDay(finalResult, 0, true);
      },
    );
    this.finale.start();
  }

  private finishDay(result: DayResult, presentationDelay = 500, campaignComplete = false): void {
    if (this.complete) return;
    this.complete = true;
    this.input.enabled = false;
    this.playerTarget = undefined;
    this.onPlayerArrival = undefined;
    this.save.recordResult(result);
    this.audio.play(result.success ? 'success' : 'angry');
    if (campaignComplete) {
      emitCampaignComplete(this.save.campaignStats);
    } else if (presentationDelay > 0) this.time.delayedCall(presentationDelay, () => emitDayComplete(result));
    else emitDayComplete(result);
  }

  private registerDebugTools(): void {
    window.__littleCornerDebug = {
      addMoney: (amount = 25) => {
        this.earnings += amount;
        this.emitHud();
      },
      addCafeMoney: (amount = 100) => this.save.addMoney(amount),
      spawnCustomer: (order) => this.spawnCustomer({
        at: this.elapsed,
        archetype: 'STANDARD',
        order: order ?? this.day.menu[Math.floor(Math.random() * this.day.menu.length)],
      }),
      lowerPatience: () => {
        const first = [...this.customers.values()].find((view) => !['LEAVING', 'ANGRY_LEAVING'].includes(view.model.stateMachine.state));
        if (first) first.model.patience = Math.min(first.model.patience, 8);
      },
      refillPatience: () => {
        this.customers.forEach((view) => { view.model.patience = view.model.maxPatience; });
      },
      setPatienceFrozen: (frozen: boolean) => {
        this.patienceFrozen = frozen;
        if (frozen) this.customers.forEach((view) => { view.model.patience = view.model.maxPatience; });
      },
      forceFoodReady: () => {
        this.prep.forceReady();
        this.updatePrepDisplay();
        this.audio.play('ready');
      },
      setSpeed: (speed: number) => {
        this.simSpeed = Phaser.Math.Clamp(speed, 0.5, 60);
        this.time.timeScale = this.simSpeed;
        this.tweens.timeScale = this.simSpeed;
      },
      triggerGorkem: () => {
        if (!this.gorkem) this.gorkem = new GorkemEventSystem(this, this.audio, () => this.handleGorkem(), (message, tone) => emitToast(message, tone));
        const event: SpecialEventDefinition = this.day.specialEvents?.[0] ?? { at: this.elapsed, type: 'GORKEM_VISIT', order: 'lemonade', bonus: 18 };
        this.gorkem.start(event);
      },
      toggleFevzi: () => {
        if (this.helper) this.helper.enabled = !this.helper.enabled;
        emitToast(this.helper?.enabled ? 'Fevzi yardımı açık.' : 'Fevzi yardımı kapalı.', 'info');
      },
      startHelperAction: () => this.helper?.trigger(() => this.getHelperTask()),
      setHitboxesVisible: (visible: boolean) => this.setHitboxesVisible(visible),
      clearCustomerSelection: () => { this.selectedCustomerId = null; },
      setCharacterTestState: (character, state, direction) => {
        const target = character === 'yasemin' ? this.player : character === 'fevzi' ? this.fevzi : this.gorkem?.visual;
        if (character === 'yasemin') this.playerTarget = undefined;
        if (target) {
          if (character !== 'yasemin') this.tweens.killTweensOf(target);
          setCharacterAnimation(target, state, direction);
        }
      },
      finishDay: () => this.finishDay({
        day: this.day.id,
        earnings: Math.max(this.earnings, this.day.goal),
        tips: this.tips,
        served: this.served,
        lost: this.lost,
        bonus: 12 + this.specialBonus,
        maxCombo: this.maxCombo,
        stars: 2,
        success: true,
      }, 0),
      triggerFinale: () => {
        if (!this.day.finale) {
          emitToast('Final senaryosu yalnızca Gün 20’de çalışır.', 'warning');
          return;
        }
        this.customers.forEach(({ container }) => container.destroy(true));
        this.customers.clear();
        this.entranceQueue.clear();
        this.prep.clear();
        this.tray.clear();
        this.updatePrepDisplay();
        this.updateTrayVisual();
        this.tables.forEach((table) => {
          table.customerId = null;
          table.state = 'EMPTY';
          table.helperClaimed = false;
          updateTableVisual(table.visual, 'EMPTY');
        });
        this.nextWaveIndex = this.day.waves.length;
        this.earnings = Math.max(this.earnings, this.day.goal);
        this.startFinale();
      },
      inspect: () => ({
        day: this.day.id,
        elapsed: Number(this.elapsed.toFixed(1)),
        earnings: this.earnings,
        cafeMoney: this.save.snapshot.cafeMoney,
        maxCombo: this.maxCombo,
        hotDrinksServed: this.hotDrinksServed,
        specialBonus: this.specialBonus,
        activeTableCount: this.tables.length,
        unlockedProducts: this.day.menu,
        patienceFrozen: this.patienceFrozen,
        actionBusy: this.actionBusy,
        complete: this.complete,
        finaleState: this.finale?.state ?? 'INACTIVE',
        selectedCustomerId: this.selectedCustomerId,
        lastPointerTarget: this.lastPointerTarget,
        queue: {
          visibleCount: this.entranceQueue.visible.length,
          pendingCount: this.entranceQueue.pending.length,
          visibleIds: [...this.entranceQueue.visible],
          pending: this.entranceQueue.pending.map(({ id, model }) => ({
            id,
            state: model.stateMachine.state,
            patience: Number(model.patience.toFixed(1)),
            maxPatience: model.maxPatience,
          })),
        },
        playerVisuals: {
          x: Number(this.player.x.toFixed(1)),
          y: Number(this.player.y.toFixed(1)),
          rootScaleX: this.player.scaleX,
          spriteScaleX: (this.player.getData('sprite') as Phaser.GameObjects.Sprite).scaleX,
          spriteFlipX: (this.player.getData('sprite') as Phaser.GameObjects.Sprite).flipX,
          animationKey: (this.player.getData('sprite') as Phaser.GameObjects.Sprite).anims.currentAnim?.key ?? null,
          frameIndex: (this.player.getData('sprite') as Phaser.GameObjects.Sprite).frame.name,
          direction: this.player.getData('direction') ?? 'down',
          uiScaleX: (this.player.getData('ui') as Phaser.GameObjects.Container).scaleX,
          facing: this.player.getData('facing') ?? 1,
        },
        prep: this.prep.tickets,
        tray: this.tray.items,
        tables: this.tables.map(({ id, state, customerId, x, y, helperClaimed }) => ({ id, state, customerId, x, y, helperClaimed })),
        helper: this.helper ? {
          enabled: this.helper.enabled,
          state: this.helper.stateMachine.state,
          actions: this.helper.actionCount,
          direction: this.fevzi.getData('direction') ?? 'down',
          spriteFlipX: (this.fevzi.getData('sprite') as Phaser.GameObjects.Sprite).flipX,
          animationKey: (this.fevzi.getData('sprite') as Phaser.GameObjects.Sprite).anims.currentAnim?.key ?? null,
          frameIndex: (this.fevzi.getData('sprite') as Phaser.GameObjects.Sprite).frame.name,
        } : null,
        gorkem: this.gorkem ? {
          state: this.gorkem.state,
          x: Number(this.gorkem.visual.x.toFixed(0)),
          y: Number(this.gorkem.visual.y.toFixed(0)),
          visible: this.gorkem.visual.visible,
          direction: this.gorkem.visual.getData('direction') ?? 'down',
          spriteFlipX: (this.gorkem.visual.getData('sprite') as Phaser.GameObjects.Sprite).flipX,
          animationKey: (this.gorkem.visual.getData('sprite') as Phaser.GameObjects.Sprite).anims.currentAnim?.key ?? null,
          frameIndex: (this.gorkem.visual.getData('sprite') as Phaser.GameObjects.Sprite).frame.name,
        } : null,
        customers: [...this.customers.values()].map((view) => ({
          id: view.model.id,
          type: view.model.archetype,
          orders: view.model.orders,
          state: view.model.stateMachine.state,
          patience: Number(view.model.patience.toFixed(1)),
          tableId: view.model.tableId,
          x: Number(view.container.x.toFixed(0)),
          y: Number(view.container.y.toFixed(0)),
          rootScaleX: view.container.scaleX,
          spriteScaleX: (view.container.getData('sprite') as Phaser.GameObjects.Container).scaleX,
          spriteFlipX: (view.container.getData('sprite') as Phaser.GameObjects.Sprite).flipX ?? false,
          animationKey: (view.container.getData('sprite') as Phaser.GameObjects.Sprite).anims?.currentAnim?.key ?? null,
          visualId: view.container.getData('customerVisualId'),
          visualState: view.container.getData('customerVisualState') ?? 'placeholder',
          productionArt: view.container.getData('productionCustomerArt') ?? false,
          uiScaleX: (view.container.getData('ui') as Phaser.GameObjects.Container).scaleX,
          bubbleScaleX: (view.container.getData('bubble') as Phaser.GameObjects.Container).scaleX,
          visualBounds: (() => {
            const bounds = view.container.getData('visualLocalBounds') as Phaser.Geom.Rectangle;
            return {
              x: view.container.x + bounds.x * view.container.scaleX,
              y: view.container.y + bounds.y * view.container.scaleY,
              width: bounds.width * view.container.scaleX,
              height: bounds.height * view.container.scaleY,
            };
          })(),
          hitArea: (() => {
            const area = view.container.getData('interactionHitArea') as Phaser.Geom.Rectangle;
            return { x: view.container.x + area.x, y: view.container.y + area.y, width: area.width, height: area.height };
          })(),
          queueSlot: view.container.getData('queueSlot') ?? null,
        })),
      }),
    };
  }
}
